import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router';
import { ArrowLeft, Eye, Settings2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Table, TableHeader, TableBody, TableRow, TableHead, TableCell,
} from '@/components/ui/table';
import SyncProgressBar from '../../components/data-integration/SyncProgressBar';
import ExtendSchemaDialog from '../../components/data-integration/ExtendSchemaDialog';
import {
  startSyncTask,
  getSyncTaskList,
  getNormalizedTableList,
  type NormalizedTableListItem,
} from '../../services/data-integration-api';

interface SyncDashboardProps {
  normalizedTableId: string;
  tableName?: string;
  onBack?: () => void;
}

export default function SyncDashboard({ normalizedTableId, tableName, onBack }: SyncDashboardProps) {
  const navigate = useNavigate();
  const [ntInfo, setNtInfo] = useState<NormalizedTableListItem | null>(null);
  const [tasks, setTasks] = useState<any[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [activeSyncTaskId, setActiveSyncTaskId] = useState<string | null>(null);
  const [syncType, setSyncType] = useState<'full' | 'incremental'>('incremental');
  const [isExtendSchemaOpen, setIsExtendSchemaOpen] = useState(false);

  const fetchNtInfo = useCallback(async () => {
    try {
      const result = await getNormalizedTableList({ pageSize: 100 });
      const found = result.items.find(t => t._id === normalizedTableId);
      if (found) setNtInfo(found);
    } catch (e) {
      console.error('获取表信息失败:', e);
    }
  }, [normalizedTableId]);

  const fetchTasks = useCallback(async () => {
    setLoading(true);
    try {
      const result = await getSyncTaskList({ normalizedTableId, page, pageSize: 20 });
      setTasks(result.items);
      setTotal(result.total);
    } catch (e) {
      console.error('获取任务列表失败:', e);
    } finally {
      setLoading(false);
    }
  }, [normalizedTableId, page]);

  useEffect(() => {
    fetchNtInfo();
    fetchTasks();
  }, [fetchNtInfo, fetchTasks]);

  const handleStartSync = async () => {
    setSyncing(true);
    try {
      const result = await startSyncTask({ normalizedTableId, syncType });
      setActiveSyncTaskId(result.syncTaskId);
    } catch (e: any) {
      alert('启动同步失败: ' + e.message);
    } finally {
      setSyncing(false);
    }
  };

  const handleSyncComplete = () => {
    setActiveSyncTaskId(null);
    fetchTasks();
  };

  const statusLabel: Record<string, string> = {
    pending: '等待中', running: '同步中', completed: '完成',
    failed: '失败', partial: '部分成功',
  };

  const statusVariant = (status: string): 'default' | 'destructive' | 'secondary' => {
    if (status === 'completed') return 'default';
    if (status === 'failed') return 'destructive';
    return 'secondary';
  };

  const formatTime = (ts: number) => ts ? new Date(ts).toLocaleString() : '-';

  return (
    <div>
      <div className="flex items-center justify-between border-b border-border bg-card px-6 py-3">
        <div className="flex items-center gap-3">
          {onBack && (
            <Button variant="ghost" size="sm" onClick={onBack}>
              <ArrowLeft className="h-4 w-4" />
            </Button>
          )}
          <h2 className="text-base font-semibold">数据同步 {tableName && `— ${tableName}`}</h2>
          {ntInfo && <span className="text-sm text-muted-foreground font-mono">({ntInfo.normalized_table_name})</span>}
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setIsExtendSchemaOpen(true)}
            title="扩展字段并配置映射"
          >
            <Settings2 className="h-4 w-4 mr-1" /> 架构扩展
          </Button>
          <div className="w-[1px] h-4 bg-border mx-1" />
          <select
            value={syncType}
            onChange={e => setSyncType(e.target.value as any)}
            className="h-9 rounded-md border border-input bg-background px-3 text-sm"
          >
            <option value="incremental">增量同步</option>
            <option value="full">全量同步</option>
          </select>
          <Button onClick={handleStartSync} disabled={syncing || !!activeSyncTaskId}>
            {syncing ? '启动中...' : '同步数据'}
          </Button>
          {tasks.some(t => t.status === 'completed') && (
            <Button
              variant="outline"
              onClick={() => navigate(`/data-integration/audit/${normalizedTableId}`)}
            >
              <Eye className="h-4 w-4 mr-1" />
              查看数据
            </Button>
          )}
        </div>
      </div>

      <div className="p-6">
        {activeSyncTaskId && (
          <SyncProgressBar
            syncTaskId={activeSyncTaskId}
            onComplete={handleSyncComplete}
          />
        )}

        {isExtendSchemaOpen && ntInfo && (
          <ExtendSchemaDialog
            open={isExtendSchemaOpen}
            onOpenChange={setIsExtendSchemaOpen}
            normalizedTableId={normalizedTableId}
            sourceTableCatalogId={ntInfo.source_table_catalog_id}
            targetConnectionId={ntInfo.target_connection_id}
            onSuccess={() => {
              // 架构扩展成功后，自动跳转到映射绑定页，并带有“增量模式”标识和目标连接ID
              navigate(`/data-integration/mapping/${ntInfo.source_table_catalog_id}?targetConnectionId=${ntInfo.target_connection_id}&incremental=true`);
            }}
          />
        )}

        <h3 className="font-semibold mb-2 mt-6">同步历史</h3>
        {loading ? (
          <p className="text-muted-foreground">加载中...</p>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>类型</TableHead>
                <TableHead>状态</TableHead>
                <TableHead>开始时间</TableHead>
                <TableHead>结束时间</TableHead>
                <TableHead>总计</TableHead>
                <TableHead>成功</TableHead>
                <TableHead>失败</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {tasks.map(t => (
                <TableRow key={t._id}>
                  <TableCell>{t.sync_type === 'full' ? '全量' : '增量'}</TableCell>
                  <TableCell>
                    <Badge variant={statusVariant(t.status)}>
                      {statusLabel[t.status] || t.status}
                    </Badge>
                  </TableCell>
                  <TableCell>{formatTime(t.start_time)}</TableCell>
                  <TableCell>{formatTime(t.end_time)}</TableCell>
                  <TableCell>{t.total_count?.toLocaleString()}</TableCell>
                  <TableCell className="text-[var(--color-success)]">{t.success_count?.toLocaleString()}</TableCell>
                  <TableCell className={t.failure_count > 0 ? 'text-destructive' : ''}>
                    {t.failure_count?.toLocaleString()}
                  </TableCell>
                </TableRow>
              ))}
              {tasks.length === 0 && (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-6 text-muted-foreground">
                    暂无同步记录
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        )}
      </div>
    </div>
  );
}
